// TestingQualityDetail.jsx
// Left TOC + scroll-spy + MAIN detailed "Template + Facebook-like Home Feed Example" per section
// Right Sponsored (AdSense). Bootstrap UI. React Router v4/v5 compatible.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ====================================================================== */
/*  AdSense                                                               */
/* ====================================================================== */

const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
      return;
    }

    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ====================================================================== */
/*  Small layout helpers (readability-first)                               */
/* ====================================================================== */

function Section({ id, title, subtitle, children }) {
  return (
    <section id={id} className="mb-5">
      <div className="mb-2">
        <h2 className="h4 mb-1">{title}</h2>
        {subtitle ? <div className="text-muted">{subtitle}</div> : null}
      </div>

      {children}

      <div className="mt-3">
        <a href="#top" className="small text-muted">
          ↑ Back to top
        </a>
      </div>
    </section>
  );
}

function Sub({ title, right }) {
  return (
    <div className="d-flex align-items-start justify-content-between gap-2 mt-3 mb-2">
      <h3 className="h6 text-uppercase text-muted mb-0">{title}</h3>
      {right}
    </div>
  );
}

function Badge({ tone = "secondary", children }) {
  return (
    <span className={`badge bg-${tone}`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function Muted({ children }) {
  return (
    <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
      {children}
    </div>
  );
}

function Card({ title, badge, children, className = "" }) {
  return (
    <div className={`card border-0 shadow-sm ${className}`}>
      <div className="card-body">
        {(title || badge) && (
          <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
            <div className="fw-semibold">{title}</div>
            {badge ? badge : null}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

function Template({ title = "Template", children }) {
  return (
    <Card
      className="mt-3"
      title={title}
      badge={<Badge tone="secondary">Template</Badge>}
    >
      <Muted>{children}</Muted>
    </Card>
  );
}

function Example({ title = "Example (Facebook-like Home Feed)", children }) {
  return (
    <Card
      className="mt-3"
      title={title}
      badge={<Badge tone="success">Example</Badge>}
    >
      <Muted>{children}</Muted>
    </Card>
  );
}

function InterviewLine({ children, tone = "primary" }) {
  return (
    <div className={`alert alert-${tone} py-2 mt-3 mb-0`} role="note">
      <div className="fw-semibold small mb-1">Interview line</div>
      <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
        {children}
      </div>
    </div>
  );
}

function Pitfalls({ items }) {
  return (
    <div className="alert alert-warning small mt-3 mb-0">
      <div className="fw-semibold mb-1">Common pitfalls</div>
      <ul className="mb-0">
        {items.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

function Checklist({ title, children }) {
  return (
    <Card className="mt-3" title={title} badge={<Badge tone="info">Checklist</Badge>}>
      <pre
        className="bg-light rounded p-3 border small mb-0"
        style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
      >
        <code>{children}</code>
      </pre>
    </Card>
  );
}

function Code({ title, children }) {
  return (
    <Card className="mt-3" title={title} badge={<Badge tone="secondary">Snippet</Badge>}>
      <pre
        className="bg-light rounded p-3 border small mb-0"
        style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
      >
        <code>{children}</code>
      </pre>
    </Card>
  );
}

/* ====================================================================== */
/*  Example context (MAIN content, not left rail)                          */
/* ====================================================================== */

function ExampleContextCard({ system }) {
  return (
    <Card
      className="mb-4"
      title="Example context"
      badge={<Badge tone="primary">{system.name}</Badge>}
    >
      <div className="row g-3">
        <div className="col-12 col-lg-4">
          <div className="fw-semibold small mb-1">Read path</div>
          <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
            {system.readPath}
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="fw-semibold small mb-1">Write path</div>
          <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
            {system.writePath}
          </div>
        </div>

        <div className="col-12 col-lg-4">
          <div className="fw-semibold small mb-1">Engagement</div>
          <div className="text-muted small" style={{ whiteSpace: "pre-wrap" }}>
            {system.engagementPath}
          </div>
        </div>
      </div>

      <hr className="my-3" />

      <div className="fw-semibold small mb-1">Common risk areas</div>
      <ul className="mb-0 text-muted small">
        {system.risks.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </Card>
  );
}

/* ====================================================================== */
/*  TOC                                                                    */
/* ====================================================================== */

const TOC = [
  { id: "unit-integration-contract", label: "A) Unit, integration, contract" },
  { id: "load-chaos", label: "B) Load, stress, chaos & failure" },
  { id: "data-quality", label: "C) Data quality: schema/freshness" },
  { id: "shadow-replay", label: "D) Shadow traffic, replay, bots" },
  { id: "quality-gates", label: "E) Quality gates in CI/CD" },
  { id: "snippets", label: "Handy patterns & snippets" },
  { id: "bites", label: "Quick one-liners" },
];

/* ====================================================================== */
/*  Page                                                                    */
/* ====================================================================== */

export default function TestingQualityDetail() {
  const [activeId, setActiveId] = useState(TOC[0].id);

  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const homeFeed = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      readPath:
        "GET /feed → fetch candidate postIds → hydrate posts/media → rank → rules → cursor",
      writePath:
        "POST /post → validate → store → async fanout/index/counters/notifications",
      engagementPath:
        "POST /like | /comment → idempotent write → counters → ranking features update",
      risks: [
        "celebrity fanout storms (write amplification)",
        "cache stampedes and thundering herd",
        "ranking regressions (latency + relevance)",
        "data drift / schema drift in events/features",
      ],
    }),
    []
  );

  /* -------------------- scroll-spy -------------------- */
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Testing &amp; quality at scale</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 rounded-3 p-3 p-md-4 text-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(13,110,253,.06), rgba(32,201,151,.07))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-2">14) Testing &amp; quality at scale — detailed guide</h1>
          <p className="text-muted mb-0">
            A practical playbook for unit/integration/contract tests, load &amp; chaos, data
            quality, shadow/replay, and CI/CD gates—written as reusable templates with a
            single consistent example.
          </p>
        </div>
      </header>

      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none order-xl-1">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <Card title="On this page">
              <nav className="nav flex-column small">
                {TOC.map(({ id, label }) => (
                  <a
                    key={id}
                    href={`#${id}`}
                    className={`nav-link px-0 ${
                      activeId === id ? "fw-semibold text-primary" : ""
                    }`}
                    aria-current={activeId === id ? "true" : undefined}
                  >
                    {label}
                  </a>
                ))}
              </nav>
            </Card>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-xl-7 order-xl-2">
          {/* Example context must be in MAIN, not left rail */}
          <ExampleContextCard system={homeFeed} />

          {/* =============================================================== */}
          {/* A) Unit/Integration/Contract                                    */}
          {/* =============================================================== */}
          <Section
            id="unit-integration-contract"
            title="A) Unit, integration, and contract testing"
            subtitle="Build confidence with a predictable pyramid: fast units, real integrations, and CDC contracts so teams can ship independently."
          >
            <Sub title="What you’re trying to prove" right={<Badge tone="light" className="text-dark border">Goal</Badge>} />
            <ul className="mb-0">
              <li>
                <strong>Correctness:</strong> business logic is right (units) and your wiring/config is right (integration).
              </li>
              <li>
                <strong>Compatibility:</strong> producers/consumers don’t break each other (contract tests).
              </li>
              <li>
                <strong>Safety:</strong> idempotency + retries + pagination behavior are stable under real delivery patterns.
              </li>
            </ul>

            <Template title="Template — the testing pyramid that actually works">
{`Recommended distribution (guideline):
• Unit tests:        70–85%  (pure + deterministic, run on every PR)
• Integration tests: 10–25%  (real deps, run on PR + nightly)
• Contract tests:     5–15%  (CDC: consumer expectations verified by provider)
• E2E tests:           1–5%  (golden paths only; do not gate everything on E2E)

Rules that prevent pain:
1) Favor determinism over coverage vanity.
2) Freeze time (clock), randomness (seed), and external IO (stubs) in unit tests.
3) Use real dependencies in integration tests (Testcontainers/embedded) to catch config/protocol bugs.
4) Contracts must cover semantics (constraints, error shapes, authz behaviors), not just JSON shape.
5) Every flaky test is either fixed quickly or quarantined with a tracked ticket.`}
            </Template>

            <Example title="Example — what to test for a Facebook-like Home Feed">
{`Unit (fast, pure):
• Ranking rules: "blocked users removed", "no duplicate posts", "pinned items first"
• Cursor rules: stable ordering, cursor round-trip, monotonic pagination
• Idempotency helper: same key + same request => no duplicate side effects

Integration (real deps):
• /feed reads: candidate fetch + hydration with Redis/DB wired correctly
• Caches: stampede protections (single-flight / request coalescing) behave as expected
• Event consumption: PostCreated -> feed fanout queue -> timeline store update

Contract (CDC):
• Feed API schema: error shapes, cursor format, pagination semantics
• Post API schema: validation errors, authz errors, idempotency behavior

Thin E2E (golden):
• POST /post then GET /feed shows new post within SLA
• POST /like updates counters and does not double-count on retries`}
            </Example>

            <Pitfalls
              items={[
                "Mocking everything → tests are green but prod is red",
                "Contracts check types but miss semantic constraints (e.g., authorization rules)",
                "E2E gates become flaky and slow; people stop trusting CI",
                "Pagination/cursor tests ignore stable ordering → duplicates or missing results",
              ]}
            />

            <Checklist title="Mini checklist — service endpoints">
{`• Happy path + boundaries + error paths
• Idempotency: same key twice => one effect
• Retries: safe + bounded; no retry storms
• Pagination: stable sort + cursor round-trip + duplicates prevented
• AuthZ matrix: allow/deny by role and ownership
• Observability: logs/traces/metrics asserted for key operations`}
            </Checklist>

            <InterviewLine>
              {`“We run a pragmatic pyramid: units for logic, integrations with real deps, and CDC contracts so producers/consumers can deploy independently. E2E is thin and focused on golden paths.”`}
            </InterviewLine>
          </Section>

          {/* =============================================================== */}
          {/* B) Load/Stress/Chaos                                             */}
          {/* =============================================================== */}
          <Section
            id="load-chaos"
            title="B) Load tests, stress tests, chaos, and failure injection"
            subtitle="Prove the system meets SLOs under expected load, fails gracefully past the knee, and recovers with guardrails."
          >
            <Sub title="Types (what each one proves)" />
            <ul className="mb-0">
              <li><strong>Load (steady):</strong> meets SLO at expected peak for a sustained window.</li>
              <li><strong>Stress (ramp):</strong> find the knee; identify the failure mode (timeouts? queue overflow? DB pool?).</li>
              <li><strong>Spike:</strong> sudden burst; verifies autoscaling, caching, and rate limiting.</li>
              <li><strong>Soak:</strong> long run; catches memory/FD leaks, GC issues, slow saturation.</li>
            </ul>

            <Template title="Template — load test plan (SLO-gated)">
{`1) Model realistic traffic:
   • Mix: read-heavy vs write-heavy
   • Think time and concurrency (not just RPS)
   • Hot/cold key distribution (celebs are hot keys)

2) Define pass/fail gates (explicit):
   • p95 latency <= target
   • error rate <= threshold
   • cache hit >= target
   • DB pool saturation <= target
   • queue lag <= threshold

3) Measure the whole system:
   • app: p95/p99, CPU, memory, GC, thread/conn counts
   • deps: DB CPU, locks, pool usage, cache hit rate
   • queues: depth, lag, retry/DLQ rates

4) Run failure modes (chaos):
   • add latency to a dependency
   • inject 5xx
   • kill a pod / drop an AZ
   • observe: circuit breakers + brownout + fallback working`}
            </Template>

            <Example title="Example — Home Feed load + chaos (what we assert)">
{`Steady load (peak hour):
• Gate: p95 <= 250ms, 5xx <= 0.5%, cache hit >= 85%, DB pool < 80% for 30 minutes
• Observe: hydration latency, ranking CPU, Redis/DB p95, queue lag for fanout

Stress ramp (find the knee):
• Increase concurrency until:
  - cache hit drops, DB pool saturates, or p99 explodes
• Confirm failure mode is “graceful”:
  - requests shed (429) or partial response (brownout) instead of meltdown

Chaos:
• Inject +300ms latency to media service:
  - expect timeouts + fallback thumbnails + no cascading failure
• Kill 1 pod during canary:
  - expect retry with jitter and stable p95 after stabilization window
• Simulate celebrity spike:
  - ensure write fanout uses batching/queues; no synchronous storms`}
            </Example>

            <Pitfalls
              items={[
                "Testing only GETs (writes cause the worst storms in feeds)",
                "RPS-only tests (you need concurrency + think time)",
                "One-IP synthetic load (CDN/WAF behavior differs from real users)",
                "Chaos without guardrails (no abort switch, no blast radius limits)",
              ]}
            />

            <InterviewLine>
              {`“We gate on SLOs with steady load, then run chaos (dep latency + AZ loss). Past the knee we degrade gracefully via rate limits and brownout instead of cascading failures.”`}
            </InterviewLine>
          </Section>

          {/* =============================================================== */}
          {/* C) Data quality                                                   */}
          {/* =============================================================== */}
          <Section
            id="data-quality"
            title="C) Data quality tests (schema, freshness, completeness)"
            subtitle="Treat datasets like APIs: they need SLAs, owners, and automated checks."
          >
            <Template title="Template — dataset SLAs + automated checks">
{`Define SLAs per dataset/event stream:
• Schema compatibility: backward/forward rules + CI check
• Freshness: p95 watermark lag <= X seconds
• Completeness: counts/uniques within expected bounds
• Accuracy/invariants: business rules hold (“no negative counts”, “debits+credits=0”)
• Duplicates: <= threshold (idempotency keys)
• Privacy: PII scanning + redaction checks

Implementation:
• CI: schema compat check against registry
• Runtime: monitor lag, counts, and anomaly detection
• Reconciliation: compare source-of-truth DB vs derived stream/table
• Alerting: page the owner on SLA breach (like an API SLO)`}
            </Template>

            <Example title="Example — Home Feed data quality (events & features)">
{`Events to protect:
• PostCreated, LikeAdded, CommentAdded, FollowEdgeChanged
• RankingFeaturesUpdated (derived)

Checks:
• Schema: new fields optional with defaults; no breaking rename without upcaster
• Freshness: events visible to ranker <= 60s (p95)
• Completeness: post count in DB vs PostCreated stream within ±1%
• Duplicates: LikeAdded dup rate <= 0.1% (idempotency key enforced)
• Drift: feature distribution shift alerts (CTR, dwell time proxies)`}
            </Example>

            <Pitfalls
              items={[
                "Calling missing data ‘eventual consistency’ with no bound",
                "Measuring freshness as average only (p95/p99 matters)",
                "Silent schema drift breaks consumers weeks later",
                "No ownership → nobody fixes the pipeline when it breaks",
              ]}
            />

            <InterviewLine>
              {`“Every dataset has SLAs: schema compatibility, freshness, and completeness. Violations alert owners just like an API SLO.”`}
            </InterviewLine>
          </Section>

          {/* =============================================================== */}
          {/* D) Shadow, replay, synthetic users                                */}
          {/* =============================================================== */}
          <Section
            id="shadow-replay"
            title="D) Shadow traffic, replay tests, and synthetic users"
            subtitle="Catch regressions safely by exercising the new version with real-like traffic—without impacting users."
          >
            <Template title="Template — shadow + replay + synthetic (how to do it safely)">
{`Shadow traffic (mirroring):
• Mirror a small % of prod requests to v2 (responses discarded)
• Scrub secrets + remove side effects (no writes, or writes go to sandbox)
• Diff: status codes, key fields, latency deltas with tolerances
• Guardrails: sampling, kill switch, per-endpoint allowlist

Replay:
• Re-run historical traffic/events against a new stack
• Preserve per-key ordering where required
• Throttle to realistic rates; mask PII; validate idempotency
• Great for migrations, index rebuilds, ranking changes

Synthetic users:
• Deterministic bots run golden journeys 24/7
• Attach traces; validate SLIs; rotate creds; use sandbox tenants
• Include negative paths (invalid cursor, auth failure) to catch UX issues`}
            </Template>

            <Example title="Example — Home Feed shadow/replay/synthetic">
{`Shadow:
• Mirror 5% of GET /feed to v2 ranker (no user impact)
• Diff: top-N postIds overlap, latency p95 delta, error shapes
• Guardrail: block any writes; feature flags to disable expensive models

Replay:
• Nightly replay T-1 traffic to validate a new ranking model + cache config
• Verify: no cache stampede, p99 stays within budget, relevance metrics stable

Synthetic:
• Bot: create post → wait → fetch feed → like → fetch feed → ensure counters consistent
• Alerts: cursor failures, spike in 5xx, ranking latency regressions`}
            </Example>

            <Pitfalls
              items={[
                "Mirroring writes to prod stores (accidental side effects)",
                "Comparing floats/timestamps without tolerances (false diffs)",
                "Replay without clock isolation or ordering guarantees",
                "Bots using real user tenants (pollution + privacy risk)",
              ]}
            />

            <InterviewLine>
              {`“We shadow a slice of prod to v2 with no side effects, diff correctness/latency, and run nightly replays plus always-on synthetic users for golden journeys.”`}
            </InterviewLine>
          </Section>

          {/* =============================================================== */}
          {/* E) CI/CD gates                                                     */}
          {/* =============================================================== */}
          <Section
            id="quality-gates"
            title="E) Quality gates in CI/CD (the end-to-end pipeline)"
            subtitle="A strong pipeline is layered: fast feedback first, then realism, then safe rollout gates with auto-rollback."
          >
            <Template title="Template — CI/CD gates (ordered for speed + signal)">
{`CI (fast):
• lint/static analysis → unit tests → contract tests → integration tests (real deps) → security scans

Pre-prod (realistic):
• load test at projected peak + headroom
• chaos: dependency latency + one-AZ loss
• data smoke tests on canary datasets/streams

Prod rollout:
• canary 1% → 5% → 25% with auto-rollback
• rollback gated on:
  - p95/p99 latency delta
  - error rate delta
  - SLO burn rate

Post-deploy:
• compare dashboards (RED/USE), top queries, cache hit rates, cost deltas
• run synthetic journeys continually

Test hygiene:
• quarantine flaky tests; deflake as tracked work
• version golden fixtures; freeze clocks; deterministic seeds`}
            </Template>

            <Example title="Example — Home Feed rollout gates (what we watch)">
{`Pre-prod:
• Peak load for 30 min: p95 <= 250ms; error <= 0.5%; cache hit >= 85%; DB pool < 80%
• Chaos: add +300ms to media service; verify fallbacks and no cascade

Canary:
• Start 1% traffic to v2 ranker
• Auto-rollback if:
  - p95 increases > 20%
  - 5xx increases > 0.3%
  - burn-rate crosses threshold for 5 minutes

Post:
• Compare ranking latency distribution, cache stampede rate, and feed engagement metrics`}
            </Example>

            <InterviewLine>
              {`“Our pipeline is layered: fast CI first, realism in pre-prod, then canary with auto-rollback on SLO burn-rate. Synthetic users keep validating the feed 24/7.”`}
            </InterviewLine>
          </Section>

          {/* =============================================================== */}
          {/* Snippets                                                          */}
          {/* =============================================================== */}
          <Section id="snippets" title="Handy patterns & snippets" subtitle="Small building blocks you can reuse everywhere.">
            <Code title="Exponential backoff with full jitter (budget-aware)">
{`for attempt in 1..3:
  maxDelay = min(500ms, 50ms * 2^(attempt-1))
  sleep(rand(0, maxDelay))
  do_request()`}
            </Code>

            <Checklist title="Contract test essentials (CDC)">
{`• Required fields + semantic constraints (not just types)
• Consistent error schema: {code, message, details[]}
• Pagination: stable ordering + cursor round-trip
• Idempotency: same key => same outcome, no duplicate effect
• AuthZ semantics: allow/deny matrix by role/ownership`}
            </Checklist>

            <Checklist title="Load test recipe (API)">
{`• Mix: 80% reads (GET /feed), 15% engagement (POST /like), 5% writes (POST /post)
• Concurrency + think time: p50 200ms, p95 1s
• Hot keys: include celebrities + cold users
• Assertions:
  - p95/p99 latency
  - <0.5% 5xx
  - Redis hit >85%
  - DB pool <80%
  - consumer lag <30s`}
            </Checklist>

            <Checklist title="Data quality gates (stream)">
{`• Watermark delay p95 < 120s
• Duplicate rate < 0.1%
• Record counts within ±1% of source
• Schema compatibility (backward + forward) passes in CI`}
            </Checklist>
          </Section>

          {/* =============================================================== */}
          {/* One-liners                                                        */}
          {/* =============================================================== */}
          <Section id="bites" title="Quick one-liners (interview-ready)" subtitle="Short lines that signal senior-level testing thinking.">
            <ul className="mb-0">
              <li>“Pyramid: units fast, integrations with real deps, CDC for cross-team safety, thin E2E for golden paths.”</li>
              <li>“Load gates on SLOs; stress finds the knee; chaos proves brownout + breakers.”</li>
              <li>“Datasets have SLAs—schema, freshness, completeness—just like APIs.”</li>
              <li>“Shadow a slice of prod to v2 and diff correctness/latency before cutover.”</li>
              <li>“Synthetic users run golden journeys 24/7 with traces attached.”</li>
            </ul>

            {/* Optional in-article ad near bottom */}
            {ADS_ENABLED && (
              <div className="my-4 text-center d-print-none">
                <AdSlot
                  enabled={ADS_ENABLED}
                  adSlot="5201219465"
                  style={{ display: "block", minHeight: 90, textAlign: "center" }}
                />
              </div>
            )}
          </Section>
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-xl-block col-xl-3 d-print-none order-xl-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
